import { Button, Modal, ModalActions, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { baseUrl, fetchKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { Suspense, useMemo, useState } from "react";
import { ListMediaFoldersResponseParams } from "../../types";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { ButtonClickAnalyticsType, SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";
import UpdateMediaFoldersSkeleton from "./UpdateMediaFoldersSkeleton";
import { UpdateMediaFoldersForm } from "../Update-media-folders-form/UpdateMediaFoldersForm";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface UpdateMediaFoldersModalProps extends SharedMenuItemProps {
    /** boolean flag to indicate the modal is currently open */
    isOpen: boolean;
    /** callback to execute when clicking cancel */
    onClose: () => void;
    /** entry ids to update folders for */
    entryIds: string[];
    /** currently selected folder id in the search filters
     * used to decide if media list should be refreshed after submit
     * */
    currentFolderId: string;
    /**
     * callback for letting the app know media list should be refreshed
     */
    reloadMedia?: () => void;
}

const baseClassName = "kms-ds-update-media-folders-modal";

/**
 * update media folders modal
 * handles adding and removing entries from folders
 */
export function UpdateMediaFoldersModal({
    isOpen,
    onClose,
    entryIds,
    onCloseMenu,
    currentFolderId,
    reloadMedia,
}: UpdateMediaFoldersModalProps) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const sendButtonAnalytics = useButtonAnalytics();

    const onIsProcessing = (isProcessing: boolean) => {
        setIsProcessing(isProcessing);
    };

    const onIsDirty = (isDirty: boolean) => {
        setIsDirty(isDirty);
    };

    const dataFetcher = useMemo<() => ListMediaFoldersResponseParams>(
        () =>
            entryIds.length > 1
                ? () => ({ entryFolders: [] })
                : fetchKmsData<ListMediaFoldersResponseParams>(
                      `${baseUrl}/folders/entry/list`,
                      { entryId: entryIds[0] },
                      true
                  ),
        [entryIds]
    );

    const handleClose = () => {
        sendButtonAnalytics(
            "Add to folder - Cancel",
            ButtonClickAnalyticsType.CLOSE,
            entryIds.length === 1 ? entryIds[0] : undefined
        );
        onClose();
    };

    const sendSubmitButtonAnalytics = () => {
        sendButtonAnalytics(
            "Add to folder - Save",
            ButtonClickAnalyticsType.SAVE,
            entryIds.length === 1 ? entryIds[0] : undefined
        );
    };

    return (
        <Modal
            classes={{
                root: `${baseClassName}-root`,
                paper: `${baseClassName}-paper`,
            }}
            open={isOpen}
            fullWidth={true}
            onKeyDown={(ev) => ev.stopPropagation()}
        >
            <ModalTitle className={`${baseClassName}-title`}>
                {entryIds.length > 1 ? translate("Add to folders") : translate("Add/Remove from folders")}
            </ModalTitle>
            <ModalContent className={`${baseClassName}-content`}>
                <ErrorBoundary>
                    <Suspense fallback={<UpdateMediaFoldersSkeleton />}>
                        <UpdateMediaFoldersForm
                            fetchData={dataFetcher}
                            entryIds={entryIds}
                            currentFolderId={currentFolderId}
                            reloadMedia={reloadMedia}
                            onCloseMenu={onCloseMenu}
                            onIsDirty={onIsDirty}
                            onIsProcessing={onIsProcessing}
                        />
                    </Suspense>
                </ErrorBoundary>
            </ModalContent>
            <ModalActions className={`${baseClassName}-actions`}>
                <Button
                    onClick={handleClose}
                    variant={"borderless"}
                    color={"secondary"}
                    className={`${baseClassName}-actions-cancel`}
                >
                    {translate("Cancel")}
                </Button>
                <Button
                    type="submit"
                    form={"update-media-folders"}
                    loading={isProcessing}
                    disabled={isProcessing || !isDirty}
                    className={`${baseClassName}-actions-submit`}
                    onClick={sendSubmitButtonAnalytics}
                >
                    {translate("Update")}
                </Button>
            </ModalActions>
        </Modal>
    );
}

export default UpdateMediaFoldersModal;
